import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import logoEmpresa from '../images/logo-empresa.png';

const SicInventario = ({ empresa, identificacion }) => {
    const [fecha, setFecha] = useState('');
    const [tipoCambio, setTipoCambio] = useState(1);
    const [inventario, setInventario] = useState([]);
    const [errores, setErrores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [logoBase64, setLogoBase64] = useState("");
    const [logoCargado, setLogoCargado] = useState(false);
    const [mostrarLogo] = useState(true);

    // Función para convertir imagen a Base64
    const convertirImagenABase64 = (img, maxWidth, maxHeight) => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
        }
        if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        return canvas.toDataURL('image/png');
    };

    // Función para convertir cualquier formato de fecha a objeto Date
    const convertirAFecha = (fechaFirestore) => {
        try {
            // Si es un objeto Timestamp de Firestore
            if (fechaFirestore && typeof fechaFirestore.toDate === 'function') {
                return fechaFirestore.toDate();
            }
            // Si es un string ISO
            if (typeof fechaFirestore === 'string') {
                return new Date(fechaFirestore);
            }
            // Si ya es un objeto Date
            if (fechaFirestore instanceof Date) {
                return fechaFirestore;
            }
            console.warn('Formato de fecha no reconocido:', fechaFirestore);
            return null;
        } catch (error) {
            console.error('Error convirtiendo fecha:', error, fechaFirestore);
            return null;
        }
    };

    // Cargar el logo al montar el componente
    useEffect(() => {
        const cargarLogo = () => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = logoEmpresa;
            
            img.onload = () => {
                try {
                    const base64 = convertirImagenABase64(img, 250, 200);
                    setLogoBase64(base64);
                    setLogoCargado(true);
                } catch (error) {
                    console.error("Error al convertir el logo a Base64:", error);
                    setLogoCargado(false);
                }
            };
            
            img.onerror = () => {
                console.error("Error al cargar el logo");
                setLogoCargado(false);
            };
        };

        const today = new Date().toISOString().split('T')[0];
        setFecha(today);
        cargarLogo();
    }, []);

    // Usar useCallback para memoizar fetchInventario
    const fetchInventario = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            console.log('Iniciando carga de inventario para fecha:', fecha);
            
            // 1. Obtener TODAS las transacciones de DetalleFactura
            const detalleFacturaRef = collection(db, 'DetalleFactura');
            const todasTransaccionesSnapshot = await getDocs(detalleFacturaRef);
            const todasTransacciones = todasTransaccionesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            console.log('Transacciones totales encontradas:', todasTransacciones.length);

            // 2. Obtener TODOS los productos de SicDetalleFactura
            const sicDetalleFacturaRef = collection(db, 'SicDetalleFactura');
            const sicDetalleSnapshot = await getDocs(sicDetalleFacturaRef);
            const productosSic = sicDetalleSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // 3. Crear mapa de productos para búsqueda rápida
            const mapaProductos = {};
            productosSic.forEach(producto => {
                mapaProductos[producto.codigoComercialCodigo] = producto;
            });

            // 4. Inicializar acumuladores
            const acumuladores = {};

            // 5. Crear fecha límite en UTC (hasta el final del día seleccionado)
            const fechaSeleccionada = new Date(fecha);
            const fechaLimiteUTC = new Date(Date.UTC(
                fechaSeleccionada.getUTCFullYear(),
                fechaSeleccionada.getUTCMonth(),
                fechaSeleccionada.getUTCDate() + 1, // +1 día para incluir todo el día seleccionado
                0, 0, 0, 0
            ));

            console.log('Fecha seleccionada:', fecha);
            console.log('Fecha límite UTC:', fechaLimiteUTC.toISOString());

            let transaccionesProcesadas = 0;
            let transaccionesFiltradas = 0;

            // 6. RECORRER SOLO LAS TRANSACCIONES DE DetalleFactura
            for (const transaccion of todasTransacciones) {
                transaccionesProcesadas++;
                const codigoEmpresa = transaccion.codigoEmpresa;
                
                // FILTRAR: No procesar códigos '999999999'
                if (!codigoEmpresa || codigoEmpresa === '999999999') continue;

                // FILTRAR: Verificar si el producto tiene Inventario = false
                const productoInfo = mapaProductos[codigoEmpresa];
                if (productoInfo) {
                    const tieneInventario = productoInfo.Inventario !== undefined ? productoInfo.Inventario :
                                          productoInfo.inventario !== undefined ? productoInfo.inventario :
                                          true;
                    
                    if (tieneInventario === false) {
                        continue;
                    }
                }

                // Verificar si la transacción es ANTERIOR a la fecha límite UTC
                let cumpleFecha = false;
                if (transaccion.fechaEmision) {
                    const fechaTransaccion = convertirAFecha(transaccion.fechaEmision);
                    
                    if (fechaTransaccion) {
                        // Incluir TODAS las transacciones ANTES de la fecha límite UTC
                        cumpleFecha = fechaTransaccion < fechaLimiteUTC;
                        
                        if (cumpleFecha) {
                            transaccionesFiltradas++;
                            console.log('Transacción incluida (UTC):', {
                                codigo: codigoEmpresa,
                                fechaTrans: fechaTransaccion.toISOString(),
                                fechaLimite: fechaLimiteUTC.toISOString(),
                                tipo: transaccion.tipoFactura,
                                cantidad: transaccion.cantidad
                            });
                        }
                    }
                }

                // Solo procesar si cumple con la fecha
                if (cumpleFecha) {
                    // Determinar si tiene inventario habilitado
                    const tieneInventario = productoInfo ? 
                        (productoInfo.Inventario !== undefined ? productoInfo.Inventario :
                         productoInfo.inventario !== undefined ? productoInfo.inventario : true) : true;

                    // Inicializar acumulador para este código si no existe
                    if (!acumuladores[codigoEmpresa]) {
                        acumuladores[codigoEmpresa] = {
                            cantidad: 0,
                            comprasCantidad: 0,
                            comprasMontoCRC: 0,
                            ventasCantidad: 0,
                            detalle: mapaProductos[codigoEmpresa] ? 
                                    mapaProductos[codigoEmpresa].detalle : 
                                    `Producto ${codigoEmpresa} (no encontrado en catálogo)`,
                            monedas: new Set(),
                            tieneInventario: tieneInventario
                        };
                    }

                    // OBTENER CANTIDAD Y MONTO TOTAL DE LA LÍNEA
                    const cantidad = parseFloat(transaccion.cantidad) || 0;
                    let montoTotalLinea = parseFloat(transaccion.montoTotalLinea) || 0;

                    // OBTENER INFORMACIÓN DE MONEDA Y TIPO DE CAMBIO
                    const codigoMoneda = transaccion.codigoMoneda || 'CRC';
                    const tipoCambioTransaccion = parseFloat(transaccion.tipoCambio) || 1;

                    // Registrar la moneda usada
                    acumuladores[codigoEmpresa].monedas.add(codigoMoneda);

                    // CONVERTIR MONTO TOTAL DE LA LÍNEA A COLONES
                    let montoFinal = montoTotalLinea;

                    // Si la moneda NO es CRC, convertir a colones usando el tipo de cambio de la transacción
                    if (codigoMoneda !== 'CRC' && tipoCambioTransaccion > 1) {
                        montoFinal = montoTotalLinea * tipoCambioTransaccion;
                    }

                    // SI EL USUARIO INGRESÓ UN TIPO DE CAMBIO MANUAL, DIVIDIR POR ESE VALOR
                    if (tipoCambio > 1) {
                        montoFinal = montoFinal / tipoCambio;
                    }

                    // Aplicar la lógica de acumulación según el tipo de factura
                    if (transaccion.tipoFactura === 'Compras' || 
                        transaccion.tipoFactura === 'NC Venta' || 
                        transaccion.tipoFactura === 'Compra' ||
                        transaccion.tipoFactura === 'Entrada inventario') {
                        acumuladores[codigoEmpresa].comprasCantidad += cantidad;
                        acumuladores[codigoEmpresa].comprasMontoCRC += montoFinal;
                    } 
                    else if (transaccion.tipoFactura === 'Ventas' || 
                             transaccion.tipoFactura === 'Venta' ||
                             transaccion.tipoFactura === 'NC Compra' ||
                             transaccion.tipoFactura === 'Salida inventario') {
                        acumuladores[codigoEmpresa].ventasCantidad += cantidad;
                    }

                    // Calcular cantidad neta
                    acumuladores[codigoEmpresa].cantidad = acumuladores[codigoEmpresa].comprasCantidad - acumuladores[codigoEmpresa].ventasCantidad;
                }
            }

            console.log('Transacciones procesadas:', transaccionesProcesadas);
            console.log('Transacciones filtradas (hasta la fecha):', transaccionesFiltradas);
            console.log('Acumuladores creados:', Object.keys(acumuladores).length);

            // 7. Crear el inventario final
            const inventarioFinal = [];
            const productosConError = [];

            for (const codigoEmpresa in acumuladores) {
                if (codigoEmpresa === '999999999') continue;

                if (acumuladores[codigoEmpresa].tieneInventario === false) {
                    continue;
                }

                if (acumuladores[codigoEmpresa].cantidad > 0) {
                    const costoPromedio = acumuladores[codigoEmpresa].comprasCantidad > 0 ? 
                        acumuladores[codigoEmpresa].comprasMontoCRC / acumuladores[codigoEmpresa].comprasCantidad : 0;
                    
                    const valorInventario = acumuladores[codigoEmpresa].cantidad * costoPromedio;
                    
                    inventarioFinal.push({
                        codigoEmpresa: codigoEmpresa,
                        detalle: acumuladores[codigoEmpresa].detalle,
                        cantidad: parseFloat(acumuladores[codigoEmpresa].cantidad.toFixed(2)),
                        montoCRC: parseFloat(valorInventario.toFixed(2)),
                        precioUnitarioPromedio: parseFloat(costoPromedio.toFixed(2)),
                        monedas: Array.from(acumuladores[codigoEmpresa].monedas).join(', ')
                    });
                }
                else if (acumuladores[codigoEmpresa].cantidad < 0) {
                    productosConError.push({
                        codigoEmpresa: codigoEmpresa,
                        detalle: acumuladores[codigoEmpresa].detalle,
                        cantidad: parseFloat(acumuladores[codigoEmpresa].cantidad.toFixed(2)),
                        comprasCantidad: acumuladores[codigoEmpresa].comprasCantidad,
                        ventasCantidad: acumuladores[codigoEmpresa].ventasCantidad,
                        monedas: Array.from(acumuladores[codigoEmpresa].monedas).join(', '),
                        diferencia: acumuladores[codigoEmpresa].ventasCantidad - acumuladores[codigoEmpresa].comprasCantidad
                    });
                }
            }

            // Ordenar por código
            inventarioFinal.sort((a, b) => a.codigoEmpresa.localeCompare(b.codigoEmpresa));
            productosConError.sort((a, b) => a.codigoEmpresa.localeCompare(b.codigoEmpresa));

            console.log('Inventario final:', inventarioFinal.length, 'productos');
            console.log('Errores:', productosConError.length, 'productos');

            setInventario(inventarioFinal);
            setErrores(productosConError);

        } catch (err) {
            console.error("Error al obtener el inventario:", err);
            setError("Error al cargar los datos. Por favor, inténtalo de nuevo.");
        } finally {
            setLoading(false);
        }
    }, [fecha, tipoCambio]);

    useEffect(() => {
        if (fecha) {
            fetchInventario();
        }
    }, [fecha, tipoCambio, fetchInventario]);

    const formatoNumero = (numero, decimales = 2) => {
        return new Intl.NumberFormat('es-CR', {
            minimumFractionDigits: decimales,
            maximumFractionDigits: decimales
        }).format(numero);
    };

    const formatoFecha = (fechaISO) => {
        if (!fechaISO) return 'N/A';
        try {
            const fecha = new Date(fechaISO);
            // Usar métodos UTC para obtener los componentes correctos de la fecha
            const year = fecha.getUTCFullYear();
            const month = (fecha.getUTCMonth() + 1).toString().padStart(2, '0');
            const day = fecha.getUTCDate().toString().padStart(2, '0');
            return `${day}/${month}/${year}`;
        } catch (error) {
            console.error('Error formateando fecha:', error);
            return 'N/A';
        }
    };

    const formatoFechaActual = () => {
        return new Date().toLocaleDateString('es-CR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const imprimirReporte = () => {
        const ventanaImpresion = window.open('', '_blank');
        const simboloMonetario = tipoCambio > 1 ? '$' : '₡';
        const monedaTexto = tipoCambio > 1 ? 'USD' : 'CRC';

        const totalValorInventario = inventario.reduce((sum, item) => sum + item.montoCRC, 0);
        const totalCantidad = inventario.reduce((sum, item) => sum + item.cantidad, 0);

        ventanaImpresion.document.write(`
            <html>
                <head>
                    <title>Reporte de Inventario</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        .report-header { margin-bottom: 20px; border-bottom: 2px solid #ddd; padding-bottom: 15px; display: flex; align-items: center; }
                        .logo-container { margin-right: 30px; }
                        .logo { max-width: 250px; max-height: 200px; height: auto; width: auto; }
                        .header-info { flex-grow: 1; font-size: 13px; text-align: right; }
                        table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; font-weight: bold; }
                        .align-right { text-align: right; }
                        .align-center { text-align: center; }
                        .total-section { margin-top: 20px; padding: 15px; background-color: #f9f9f9; border-radius: 8px; border: 1px solid #ddd; }
                        .total-item { display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px dashed #eee; }
                        .total-item:last-child { border-bottom: none; font-weight: bold; font-size: 14px; }
                        .mono { font-family: 'Courier New', monospace; }
                        .error-section { margin-top: 30px; padding: 15px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; }
                        .error-row { background-color: #f8d7da; }
                        .no-print { display: block; }
                        @media print {
                            body { margin: 0; padding: 15px; }
                            table { font-size: 10px; }
                            .total-section, .error-section { page-break-inside: avoid; }
                            .no-print { display: none; }
                        }
                        button { padding: 10px 20px; margin: 5px; border: 1px solid #ccc; border-radius: 5px; background-color: #f8f9fa; cursor: pointer; font-size: 14px; }
                        button:hover { background-color: #e9ecef; }
                    </style>
                </head>
                <body>
                    <div class="report-header">
                        <div class="logo-container">
                            ${logoCargado && mostrarLogo ? `<img src="${logoBase64}" class="logo" alt="Logo de ${empresa || 'la empresa'}">` : ''}
                        </div>
                        <div class="header-info">
                            <h2>Reporte de Inventario</h2>
                            <p><strong>Empresa:</strong> ${empresa || 'No especificada'}</p>
                            <p><strong>Identificación:</strong> ${identificacion || 'No especificada'}</p>
                            <p><strong>Fecha del Inventario:</strong> ${formatoFecha(fecha + 'T00:00:00Z')}</p>
                            <p><strong>Moneda:</strong> ${monedaTexto} (${simboloMonetario})</p>
                            <p><strong>Fecha de generación:</strong> ${formatoFechaActual()}</p>
                            ${tipoCambio > 1 ? `<p><strong>Tipo de cambio aplicado:</strong> 1 USD = ${tipoCambio} CRC</p>` : ''}
                        </div>
                    </div>
                    
                    <table>
                        <thead>
                            <tr>
                                <th class="align-center">Código</th>
                                <th>Detalle del Producto</th>
                                <th class="align-right">Cantidad</th>
                                <th class="align-right">Costo Unitario</th>
                                <th class="align-right">Valor Total</th>
                                <th class="align-center">Monedas</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${inventario.map(item => `
                                <tr>
                                    <td class="align-center mono">${item.codigoEmpresa}</td>
                                    <td>${item.detalle}</td>
                                    <td class="align-right mono">${formatoNumero(item.cantidad, 2)}</td>
                                    <td class="align-right mono">${simboloMonetario}${formatoNumero(item.precioUnitarioPromedio)}</td>
                                    <td class="align-right mono">${simboloMonetario}${formatoNumero(item.montoCRC)}</td>
                                    <td class="align-center">${item.monedas}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="total-section">
                        <h3>Totales del Inventario</h3>
                        <div class="total-item">
                            <span>Total de Productos:</span>
                            <span class="mono">${inventario.length}</span>
                        </div>
                        <div class="total-item">
                            <span>Total de Unidades:</span>
                            <span class="mono">${formatoNumero(totalCantidad, 2)}</span>
                        </div>
                        <div class="total-item">
                            <span>Valor Total del Inventario:</span>
                            <span class="mono">${simboloMonetario}${formatoNumero(totalValorInventario)}</span>
                        </div>
                    </div>

                    ${errores.length > 0 ? `
                    <div class="error-section">
                        <h3>⚠️ Productos con Posibles Errores: ${errores.length}</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th class="align-center">Código</th>
                                    <th>Detalle</th>
                                    <th class="align-right">Cantidad</th>
                                    <th class="align-right">Compras</th>
                                    <th class="align-right">Ventas</th>
                                    <th class="align-right">Diferencia</th>
                                    <th class="align-center">Monedas</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${errores.map(error => `
                                    <tr class="error-row">
                                        <td class="align-center mono">${error.codigoEmpresa}</td>
                                        <td>${error.detalle}</td>
                                        <td class="align-right mono" style="color: #dc3545;">${formatoNumero(error.cantidad, 2)}</td>
                                        <td class="align-right mono">${error.comprasCantidad}</td>
                                        <td class="align-right mono">${error.ventasCantidad}</td>
                                        <td class="align-right mono" style="color: #dc3545;">${error.diferencia}</td>
                                        <td class="align-center">${error.monedas}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                    ` : ''}

                    <div class="no-print">
                        <br>
                        <button onclick="window.print()">Imprimir</button>
                        <button onclick="window.close()">Cerrar</button>
                    </div>

                    <script>
                        window.focus();
                    </script>
                </body>
            </html>
        `);
        
        ventanaImpresion.document.close();
    };

    const exportarAExcel = () => {
        const datosExportar = [
            ['Código', 'Detalle', 'Cantidad', 'Valor Total', 'Costo Unitario', 'Monedas']
        ];

        inventario.forEach(item => {
            if (item.codigoEmpresa !== '999999999') {
                datosExportar.push([
                    item.codigoEmpresa,
                    item.detalle,
                    item.cantidad,
                    item.montoCRC,
                    item.precioUnitarioPromedio,
                    item.monedas
                ]);
            }
        });

        datosExportar.push([]);
        datosExportar.push(['PRODUCTOS CON ERRORES (CANTIDAD NEGATIVA)']);
        datosExportar.push(['Código', 'Detalle', 'Cantidad', 'Compras', 'Ventas', 'Diferencia', 'Monedas']);

        errores.forEach(error => {
            if (error.codigoEmpresa !== '999999999') {
                datosExportar.push([
                    error.codigoEmpresa,
                    error.detalle,
                    error.cantidad,
                    error.comprasCantidad,
                    error.ventasCantidad,
                    error.diferencia,
                    error.monedas
                ]);
            }
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(datosExportar);
        
        XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
        
        const fechaFormateada = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        XLSX.writeFile(wb, `inventario_${fechaFormateada}.xlsx`);
    };

    const simboloMonetario = tipoCambio > 1 ? '$' : '₡';
    const monedaTexto = tipoCambio > 1 ? 'USD' : 'CRC';

    return (
        <div>
            <h2>Consulta de Inventario a una Fecha</h2>
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                <div>
                    <label htmlFor="fecha">Seleccionar Fecha: </label>
                    <input
                        type="date"
                        id="fecha"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        style={{ marginRight: '20px' }}
                    />
                </div>
                <div style={{ marginTop: '10px' }}>
                    <label htmlFor="tipoCambio">Tipo de Cambio (USD a CRC): </label>
                    <input
                        type="number"
                        id="tipoCambio"
                        value={tipoCambio}
                        onChange={(e) => setTipoCambio(parseFloat(e.target.value) || 1)}
                        step="0.01"
                        min="1"
                        placeholder="1.00"
                        style={{ width: '100px' }}
                    />
                    <small style={{ marginLeft: '10px', color: '#666' }}>
                        {tipoCambio > 1 ? `1 USD = ${tipoCambio} CRC` : 'Mostrando en colones (CRC)'}
                    </small>
                </div>
            </div>

            {loading && <p>Cargando datos...</p>}
            {error && <p className="error">{error}</p>}

            {!loading && !error && (inventario.length > 0 || errores.length > 0) && (
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                    <button 
                        onClick={exportarAExcel}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        📊 Exportar a Excel
                    </button>
                    <button 
                        onClick={imprimirReporte}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        🖨️ Imprimir Reporte
                    </button>
                </div>
            )}

        {!loading && !error && inventario.length > 0 && (
            <div>
                <p>Mostrando {inventario.length} productos con inventario disponible</p>
                <p><strong>Moneda: {monedaTexto} ({simboloMonetario})</strong></p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '30px' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Código</th>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Detalle</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Cantidad</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Valor Total</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Costo Unitario</th>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Monedas</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inventario.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                <td style={{ padding: '12px' }}>{item.codigoEmpresa}</td>
                                <td style={{ padding: '12px' }}>{item.detalle}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>
                                    {item.cantidad}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>
                                    {simboloMonetario}{item.montoCRC?.toFixed(2) || '0.00'}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>
                                    {simboloMonetario}{item.precioUnitarioPromedio?.toFixed(2) || '0.00'}
                                </td>
                                <td style={{ padding: '12px' }}>{item.monedas}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

        {!loading && !error && errores.length > 0 && (
            <div>
                <h3 style={{ color: '#dc3545' }}>⚠️ Productos con posibles errores: {errores.length}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#fff3cd' }}>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Código</th>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Detalle</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Cantidad</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Compras</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Ventas</th>
                            <th style={{ textAlign: 'right', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Diferencia</th>
                            <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #dee2e6' }}>Monedas</th>
                        </tr>
                    </thead>
                    <tbody>
                        {errores.map((error, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#f8d7da' }}>
                                <td style={{ padding: '12px' }}>{error.codigoEmpresa}</td>
                                <td style={{ padding: '12px' }}>{error.detalle}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px', color: '#dc3545' }}>
                                    {error.cantidad}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>
                                    {error.comprasCantidad}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>
                                    {error.ventasCantidad}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontFamily: 'monospace', fontSize: '14px', color: '#dc3545' }}>
                                    {error.diferencia}
                                </td>
                                <td style={{ padding: '12px' }}>{error.monedas}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

        {!loading && !error && inventario.length === 0 && errores.length === 0 && (
            <div>
                <p>No hay productos con inventario disponible para la fecha seleccionada.</p>
            </div>
        )}
    </div>
);
};

export default SicInventario;